import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const KEY = "sacrvm.admin.token";

const TABS = [
  { key: "contacts", label: "Contact" },
  { key: "private_label", label: "Private Label" },
  { key: "product_inquiries", label: "Product Inquiry" },
  { key: "gifting", label: "Corporate Gifting" },
  { key: "distributors", label: "Distributor" },
];

export default function AdminLeads() {
  const [token, setToken] = useState(() => localStorage.getItem(KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("contacts");
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "SACRVM · Leads";
    document.body.style.background = "#0F0F0F";
  }, []);

  const load = async (tok) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/leads?limit=500", { headers: { "X-Admin-Token": tok } });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.status === 401 ? "Token inválido o expirado." : "Error de red.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const rows = useMemo(() => {
    if (!data) return [];
    const list = data[tab] || [];
    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [data, tab, query]);

  const counts = useMemo(() => {
    if (!data) return {};
    return Object.fromEntries(TABS.map((t) => [t.key, (data[t.key] || []).length]));
  }, [data]);

  const submitToken = (e) => {
    e.preventDefault();
    localStorage.setItem(KEY, tokenInput.trim());
    setToken(tokenInput.trim());
  };

  const logout = () => {
    localStorage.removeItem(KEY);
    setToken("");
    setTokenInput("");
    setData(null);
  };

  const downloadCsv = () => {
    if (!rows.length) return;
    const keys = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sacrvm-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-[#0F0F0F] text-white flex items-center justify-center p-6">
        <form onSubmit={submitToken} className="w-full max-w-[420px]">
          <div className="font-serif text-white text-[36px] mb-2 tracking-[0.06em]">SACRVM</div>
          <div className="overline mb-10">Leads Console</div>
          <div className="field filled">
            <label>Admin token</label>
            <input
              data-testid="admin-token-input"
              type="password"
              autoFocus
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              required
            />
          </div>
          <button data-testid="admin-login-btn" type="submit" className="btn-sharp gold mt-8">
            Enter
          </button>
          {error && <p className="mt-6 text-[color:var(--sacrvm-copper)] text-[13px]">{error}</p>}
          <p className="mt-10 text-white/30 text-[11px] tracking-[0.24em] uppercase">Private · SACRVM</p>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0F0F0F] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-[#0F0F0F]/90 backdrop-blur-xl z-10">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 flex items-center justify-between h-[72px]">
          <a href="/" className="font-serif tracking-[0.28em] text-[18px]">SACRVM</a>
          <div className="hidden md:block overline !text-white/40">Leads Console</div>
          <div className="flex items-center gap-4">
            <button onClick={() => load(token)} className="text-[11px] tracking-[0.24em] uppercase text-white/70 hover:text-[color:var(--sacrvm-gold)]">
              {loading ? "Loading…" : "Refresh"}
            </button>
            <button onClick={downloadCsv} disabled={!rows.length} className="text-[11px] tracking-[0.24em] uppercase text-white/70 hover:text-[color:var(--sacrvm-gold)] disabled:opacity-30">
              CSV
            </button>
            <button data-testid="admin-logout" onClick={logout} className="text-[11px] tracking-[0.24em] uppercase text-white/70 hover:text-[color:var(--sacrvm-gold)]">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-6 md:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-t border-white/10">
          {TABS.map((tt) => (
            <button
              key={tt.key}
              data-testid={`admin-tab-${tt.key}`}
              onClick={() => setTab(tt.key)}
              className={
                "border-b md:border-b-0 md:border-r border-white/10 last:border-r-0 px-4 py-6 text-left transition-colors " +
                (tab === tt.key ? "bg-white/[0.04]" : "hover:bg-white/[0.02]")
              }
            >
              <div className="overline mb-2 !text-white/40">{tt.label}</div>
              <div className="font-serif text-[28px] md:text-[34px] text-white">
                {counts[tt.key] ?? 0}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-serif text-white text-[22px] md:text-[28px]">
            {TABS.find((t) => t.key === tab)?.label} <span className="text-white/40">· {rows.length}</span>
          </div>
          <input
            data-testid="admin-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search email, name, message…"
            className="w-full md:w-[360px] bg-transparent border-b border-white/20 focus:border-[color:var(--sacrvm-gold)] outline-none py-2 text-[14px] placeholder:text-white/30"
          />
        </div>

        {error && <p className="mt-6 text-[color:var(--sacrvm-copper)] text-[13px]">{error}</p>}

        <div className="mt-6 overflow-x-auto">
          {rows.length === 0 ? (
            <div className="py-24 text-center text-white/40 text-[14px]">No records yet.</div>
          ) : (
            <table className="w-full text-[13px]" data-testid="admin-table">
              <thead>
                <tr className="text-left text-white/40 uppercase tracking-[0.18em] text-[10px]">
                  {Object.keys(rows[0]).filter((k) => k !== "kind").map((k) => (
                    <th key={k} className="py-3 pr-6 border-b border-white/10 font-medium">{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-white/[0.02] align-top">
                    {Object.keys(rows[0]).filter((k) => k !== "kind").map((k) => (
                      <td key={k} className="py-4 pr-6 border-b border-white/5 text-white/85 max-w-[320px] break-words">
                        {k === "created_at" ? new Date(r[k]).toLocaleString() : String(r[k] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
